import { useQueries, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import type { Message } from '@/domain/message.types'
import { pendingVariants, TRANSLATION_EVENTS, translationGroupKey, translationGroupQuery } from '@/hooks/useMessages'
import { RealtimeConnectionState, subscribeRealtime, useRealtimeConnectionState } from '@/realtime/realtime-dispatcher'

/** One group's variants and whether its read is still in flight. */
export interface MatrixGroup {
	variants: Message[]
	isLoading: boolean
}

/**
 * `messageId|translationGroupId` pairs, joined into one string. The variant
 * arrays are re-allocated on every refetch, so an array dependency would
 * re-register the realtime subscription each time; joining makes an unchanged
 * set compare equal.
 */
const encodePendingPairs = (variants: readonly Message[]): string =>
	pendingVariants(variants)
		.map((v) => `${v.messageId}|${v.translationGroupId}`)
		.join(',')

/**
 * Every listed group's locale variants, read as one independent query per group
 * (`translationGroupQuery`, shared with the detail page's `useTranslationGroup`)
 * so a slow group never blocks the rest of the matrix from rendering.
 *
 * Waiting for in-flight translations follows ADR-0020 exactly as the detail page
 * does, with one difference that matters at this scale: when connected, the
 * matrix opens a SINGLE subscription covering every pending variant across every
 * row, rather than one per row. An arriving event invalidates only the group it
 * names, so one finished translation does not refetch every other group.
 */
export function useMatrixGroups(sourceMessages: readonly Message[]): {
	groups: Map<string, MatrixGroup>
	allVariants: Message[]
} {
	const queryClient = useQueryClient()
	const isPushing = useRealtimeConnectionState() === RealtimeConnectionState.CONNECTED

	const results = useQueries({
		queries: sourceMessages.map((source) => translationGroupQuery(source.translationGroupId, isPushing)),
	})

	// Seed each group with its own source row so the source column renders
	// immediately, before the group read lands.
	const groups = new Map<string, MatrixGroup>()
	sourceMessages.forEach((source, index) => {
		const result = results[index]
		groups.set(source.translationGroupId, {
			variants: result?.data ?? [source],
			isLoading: result?.isLoading ?? true,
		})
	})
	const allVariants = [...groups.values()].flatMap((group) => group.variants)
	const pendingPairs = encodePendingPairs(allVariants)

	useEffect(() => {
		if (!isPushing || !pendingPairs) return
		const groupByMessageId = new Map(pendingPairs.split(',').map((pair) => pair.split('|') as [string, string]))

		// Invalidate the whole group the event names, not just the row: a variant
		// finishing changes the group's aggregate state, and re-reading is what
		// produces the translated text — the event does not carry it.
		return subscribeRealtime(
			{ type: TRANSLATION_EVENTS, entityRef: { id: [...groupByMessageId.keys()] } },
			(message) => {
				const translationGroupId = groupByMessageId.get(message.entityRef.id)
				if (!translationGroupId) return
				void queryClient.invalidateQueries({ queryKey: translationGroupKey(translationGroupId) })
			},
		)
	}, [isPushing, pendingPairs, queryClient])

	return { groups, allVariants }
}
